import React, {
  ChangeEvent,
  ReactElement,
  Ref,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';

import { usePrevious } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import { MultiAutocompleteControlProps } from './MultiAutocompleteControl';
import { defaultLabelGetter } from '../../helpers';
import useAutocompleteControl from '../../hooks/useAutocompleteControl';
import { ControlContainer, SearchInput, Token } from '../../layout';
import { GenericItem } from '../../types';

const defaultRenderTokens = <TItem extends GenericItem>({
  selectedItems,
  itemToString,
  removeSelectedItem,
  getSelectedItemProps,
  disabled: controlDisabled,
}: Pick<
  MultiAutocompleteControlProps<TItem>,
  | 'selectedItems'
  | 'itemToString'
  | 'removeSelectedItem'
  | 'getSelectedItemProps'
  | 'disabled'
>) =>
  selectedItems?.map((selectedItem: TItem, index: number) => {
    const {
      style: tokenStyle,
      css: _ignored,
      disabled: itemDisabled,
      ...selectedItemProps
    } = getSelectedItemProps({
      selectedItem,
      index,
    });

    return (
      <Token
        key={`selected-item-${index}`}
        style={tokenStyle as StyleProp}
        onClickX={() => removeSelectedItem(selectedItem)}
        onRemove={() => removeSelectedItem(selectedItem)}
        {...selectedItemProps}
        content={itemToString?.(selectedItem)}
        disabled={itemDisabled || controlDisabled}
      />
    );
  });

const MultiAutocompleteTokenControlWrapped = <TItem extends GenericItem>(
  {
    selectedItems,
    isOpen,
    disabled,
    getInputProps,
    removeSelectedItem,
    getSelectedItemProps,
    itemToString = defaultLabelGetter,
    openMenu,
    renderItems = defaultRenderTokens,
    style,
    placeholder: placeholderProp,
    searchPlaceholder: searchPlaceholderProp,
    ...props
  }: MultiAutocompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const hasSelectedItems = selectedItems?.length
    ? selectedItems.length > 0
    : false;
  const prevSelectedItems = usePrevious(selectedItems);
  const { tokens } = useTheme();
  const iconTokens = tokens?.component.dropdown?.control?.icon;

  const { containerProps, searchInputProps } = useAutocompleteControl({
    placeholderProp,
    searchPlaceholderProp,
    hasTokens: hasSelectedItems,
    isOpen,
    disabled,
    openMenu,
    selectedItems,
  });

  const [inputStyle, setInputStyle] = useState({
    width: '0ch',
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen && containerRef?.current) {
      // Scroll to top when the dropdown is closed
      containerRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    // ensure that control is scrolled to the bottom only when items are added
    if (
      containerRef?.current &&
      (prevSelectedItems?.length ?? 0) < (selectedItems?.length ?? 0)
    ) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [prevSelectedItems?.length, selectedItems]);

  const resizeInput = (value: string) => {
    setInputStyle({
      width: `${value.length}ch`,
    });
  };

  const inputOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => resizeInput(e.target.value),
    [],
  );

  const inputProps = useMemo(
    () =>
      getInputProps?.({
        onChange: inputOnChange,
        onKeyDown: (e) => {
          if ((e.target as HTMLInputElement).value.length) {
            // there is text in the input, don't delete an item
            return;
          }

          if (!selectedItems?.length) {
            // No selected items to delete
            return;
          }

          const lastSelectedItem = selectedItems[selectedItems.length - 1];

          const { disabled: lastItemDisabled } = getSelectedItemProps({
            selectedItem: lastSelectedItem,
          });

          if (lastItemDisabled) {
            return;
          }

          if (e.key === 'Backspace') {
            removeSelectedItem(lastSelectedItem);
          }
        },
        ...searchInputProps,
      }) || { style: undefined, placeholder: undefined },
    [
      getInputProps,
      inputOnChange,
      selectedItems,
      removeSelectedItem,
      getSelectedItemProps,
      searchInputProps,
    ],
  );

  const showInput = useMemo(
    () =>
      !selectedItems ||
      selectedItems.length <= 0 ||
      inputProps?.placeholder ||
      (selectedItems && isOpen),
    [selectedItems, isOpen, inputProps],
  );

  return (
    <ControlContainer
      ref={ref}
      style={[
        {
          maxHeight: '6.25em',
          overflow: 'hidden',
        },
        style,
      ]}
      {...containerProps}
      {...props}
    >
      <MagnifyingGlassIcon
        size={20}
        style={[
          {
            minWidth: 20,
            display: isOpen ? 'inline' : 'none',
          },
          iconTokens?.search?._,
          { color: iconTokens?._?.color },
        ]}
      />
      <div
        ref={containerRef}
        css={[
          {
            display: 'flex',
            overflowY: 'auto',
            flexWrap: 'wrap',
            flex: 1,
            alignItems: 'center',
            maxHeight: 'inherit',
          },
        ]}
      >
        {selectedItems &&
          renderItems({
            isOpen,
            selectedItems,
            getSelectedItemProps,
            removeSelectedItem,
            itemToString,
            disabled,
          })}
        <SearchInput
          style={{ ...inputStyle, ...(!showInput && { display: 'none' }) }}
          isOpen={isOpen}
          {...inputProps}
          // disabling the built in icon as we want the icon to appear before
          // tokens in this case
          showIcon={false}
        />
      </div>
    </ControlContainer>
  );
};

const MultiAutocompleteTokenControl = React.forwardRef(
  MultiAutocompleteTokenControlWrapped,
);

MultiAutocompleteTokenControl.displayName = 'MultiAutocompleteTokenControl';

MultiAutocompleteTokenControl.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to get props for the menu toggle button */
  getToggleButtonProps: PropTypes.func.isRequired,
  /** The content of te ControlContainer */
  children: PropTypes.any,
};

export default MultiAutocompleteTokenControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
